import type {Component, Device, PropertyProps} from 'grapesjs';
import type {PrimaryConfigOptions} from '@/email-maker/grapesjs-email-editor-plugin/src';

export interface ToggleButtonProps extends PropertyProps {
  component?: Component;
  defaultValue?: number; // Renamed to avoid conflict with PropertyProps.default
  leftOptionValue: string;
  rightOptionValue: string;
  label: string;
  style?: string;
  defaultSelectedStyle?: string;
  isResponsive?: boolean;
  onToggleRight: (comp: Component) => void;
  onToggleLeft: (comp: Component) => void;
}

export default (opt: PrimaryConfigOptions): void => {
  const {editor} = opt;
  const styleManager = editor.StyleManager;
  const RENDER_DELAY_MS = 200;

  /**
   * Allow to toggle between two options
   */
  styleManager.addType(window.PMW.emails.styles.ID_TOGGLE_BUTTON, {
    create(data) {
      const {change} = data;
      const props = data.props as ToggleButtonProps;
      props.component = props.component ?? editor.getSelected();
      const currComponent = props.component;

      if (!currComponent) {
        throw new Error('No component selected for toggle button');
      }

      const toggleSelector = document.createElement('div');
      const styles = currComponent.getStyle() || {};

      let isToggled = false;

      if (props.style && styles[props.style] === props.defaultSelectedStyle) {
        isToggled = true;
      }

      toggleSelector.className = props.type ?? '';
      toggleSelector.innerHTML = getToggleButtonsHtml(props, isToggled);

      if (props.isResponsive) {
        addResponsiveDeviceEvent(props);
      }

      attachToggleButtonHandler(props, change, toggleSelector);
      return toggleSelector;
    },

    emit(data, eventData: {toggleEl: Element; toggleId: number}) {
      const props = data.props as ToggleButtonProps;
      const {toggleEl, toggleId} = eventData;

      // Replace jQuery with vanilla JavaScript
      toggleEl.classList.add('-selected-comp');
      const siblings = toggleEl.parentElement?.querySelectorAll('.toggle-option');
      siblings?.forEach(sibling => {
        if (sibling !== toggleEl) {
          sibling.classList.remove('-selected-comp');
        }
      });

      if (props.component && props.property) {
        props.component.set(props.property, toggleId);
      }

      if (props.component) {
        if (toggleId) {
          props.onToggleLeft(props.component);
        } else {
          props.onToggleRight(props.component);
        }
      }
    }
  });

  /**
   * Attaches click handlers to toggle buttons
   */
  function attachToggleButtonHandler(
    props: ToggleButtonProps,
    change: (data: {toggleSelector: HTMLElement; toggleEl: Element; toggleId: number}) => void,
    toggleSelector: HTMLElement
  ): void {
    toggleSelector.querySelectorAll('.js-toggle-buttons').forEach(toggleEl => {
      const toggleId = parseInt(toggleEl.getAttribute('data-preset-id') ?? '0', 10);

      toggleEl.addEventListener('click', function () {
        change({toggleSelector, toggleEl, toggleId});
      });
    });
  }

  /**
   * Handles responsive device changes
   */
  function onResponsiveDeviceChange(device: Device, props: ToggleButtonProps): void {
    const labelEl = document.querySelector('.js-toggle-button-label');
    if (!labelEl) return;

    const deviceName = device.get('name');
    if (!deviceName) return;

    switch (deviceName.toLowerCase()) {
      case window.PMW.emails.DEVICE_MOBILE:
        (labelEl as HTMLElement).innerHTML = getHighlightTag(deviceName) + props.label;
        labelEl.classList.add('flex-v-row');
        break;
      case window.PMW.emails.DEVICE_TABLET:
        (labelEl as HTMLElement).innerHTML = getHighlightTag(deviceName) + props.label;
        labelEl.classList.add('flex-v-row');
        break;
      case window.PMW.emails.DEVICE_DESKTOP:
        (labelEl as HTMLElement).innerHTML = props.label;
        labelEl.classList.remove('flex-v-row');
        break;
    }
  }

  function getHighlightTag(tagName: string): string {
    return `<span class="highlight-tag body-sm-bold -rounded">${tagName}</span>`;
  }

  function addResponsiveDeviceEvent(props: ToggleButtonProps): void {
    const currentDevice = editor.DeviceManager.getSelected();

    if (currentDevice) {
      setTimeout(() => {
        onResponsiveDeviceChange(currentDevice, props);
      }, RENDER_DELAY_MS);
    }

    editor.on('device:select', (newDevice: Device) => {
      onResponsiveDeviceChange(newDevice, props);
    });
  }

  /**
   * Generates HTML markup for toggle buttons
   */
  function getToggleButtonsHtml(props: ToggleButtonProps, isToggled: boolean): string {
    return `<div class="flexbox flex-content-between flex-align-items-center">
             <label class="toggle-text body-md-regular js-toggle-button-label">${props.label}</label>
            <div class="toggle-switch flexbox flex-h-row flex-content-between _fit-width border-s-dark radius-8">
            <span data-preset-id="${window.PMW.emails.TOGGLE_OPTION_PRESET_ID_ONE}" class="flexbox flex-content-center body-md-bold toggle-option js-toggle-buttons radius-4 _cursor-pointer spacing-m-l-1 spacing-m-r-1 ${isToggled ? '-selected-comp' : ''}"> ${props.leftOptionValue}</span>
            <span data-preset-id="${window.PMW.emails.TOGGLE_OPTION_PRESET_ID_ZERO}" class="flexbox flex-content-center body-md-bold toggle-option js-toggle-buttons radius-4 _cursor-pointer spacing-m-r-1 ${!isToggled ? '-selected-comp' : ''}"> ${props.rightOptionValue}</span>
            </div>
        </div>`;
  }
};